import React, { useState } from "react";
import { Check, MapPin } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/shadcn/ui/sheet";
import type { DaySheet } from "../providerApi";
import type { FieldWorkspaceProps } from "./contracts";
import { FieldSelect, Notice } from "./FieldPrimitives";
import { useViewingAs } from "./clinical/clinicalApi";

export default function Arrival({
  sheet,
  onCheckIn,
  offline,
  intro = "Start with your current manifest, capture notes, and see what needs attention.",
}: {
  sheet: DaySheet;
  onCheckIn: FieldWorkspaceProps["onCheckIn"];
  offline: boolean;
  /** The line before arrival is recorded (the Podiatry workspace says its own). */
  intro?: string;
}) {
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState(
    sheet.contacts.ssd[0]?.id || sheet.contacts.all[0]?.id || "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // A read-only "Sign in as" session cannot record an arrival: the server refuses it, so it is not offered.
  const viewing = useViewingAs();
  const latest = sheet.checkIn.today[0];
  return (
    <>
      <section className="fw-session" aria-label="Field session">
        <div>
          <span className="fw-eyebrow">
            Field session · {sheet.facility.name}
          </span>
          <h2>
            {latest
              ? "You’re checked in. Make every round count."
              : "Ready for your next facility round?"}
          </h2>
          <p>
            {latest
              ? `Arrival recorded ${new Date(latest.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}${latest.contact_name_snapshot ? ` with ${latest.contact_name_snapshot}` : " · no contact named"}.`
              : intro}
          </p>
        </div>
        <div className="fw-session-act">
          <Button
            className="fw-primary"
            onClick={() => setOpen(true)}
            disabled={offline || !!viewing}
            aria-describedby={viewing ? "fw-arrival-viewing" : undefined}
          >
            {latest ? <Check /> : <MapPin />}
            {latest ? "Record another arrival" : "Confirm arrival"}
          </Button>
          {viewing && (
            <small id="fw-arrival-viewing" data-testid="arrival-viewing">
              Read-only while viewing
            </small>
          )}
        </div>
      </section>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!busy) setOpen(next);
        }}
      >
        <SheetContent className="fw-drawer flex flex-col">
          <SheetHeader>
            <SheetTitle>Check in at {sheet.facility.name}</SheetTitle>
            <SheetDescription>
              Record your arrival and the facility contact you met.
            </SheetDescription>
          </SheetHeader>
          <form
            className="fw-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (busy || offline || viewing) return;
              setBusy(true);
              setError("");
              try {
                const ok = await onCheckIn(contact || null);
                if (ok) setOpen(false);
                else
                  setError(
                    "Arrival was not confirmed. Check your connection and try again.",
                  );
              } catch {
                setError("Arrival was not confirmed. Try again.");
              } finally {
                setBusy(false);
              }
            }}
          >
            {sheet.contacts.all.length ? (
              <div className="fw-field">
                <span id="fw-contact-label">Facility contact</span>
                <FieldSelect
                  label="Facility contact"
                  value={contact}
                  onChange={setContact}
                  options={[
                    { value: "", label: "No contact named" },
                    ...sheet.contacts.all.map((c) => ({
                      value: c.id,
                      label: `${c.name || "Name not recorded"}${c.title ? ` · ${c.title}` : ""}`,
                    })),
                  ]}
                />
              </div>
            ) : (
              <Notice>
                No facility contact is recorded. You can still record your
                arrival.
              </Notice>
            )}
            {error && (
              <p role="alert" className="fw-error">
                {error}
              </p>
            )}
            <Button
              className="fw-primary"
              disabled={busy || offline || !!viewing}
              type="submit"
            >
              {busy ? "Recording arrival…" : "Record arrival"}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
